import SEO from '../SEO';

const CancellationRefundPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-green-50">
      <SEO
        title="Cancellation & Refund Policy | MetaFit Wellness"
        description="Learn about MetaFit Wellness cancellation timelines, refund eligibility, booking rescheduling, and customer satisfaction policies."
        keywords="cancellation policy, refund policy, session refunds, booking cancellation metafit"
        url="https://www.metafitwellness.com/policy/cancellation-refund"
        canonical="https://www.metafitwellness.com/policy/cancellation-refund"
      />
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-teal-100">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <h1 className="text-4xl font-bold text-gray-800 text-center">Cancellation and Refund Policy</h1>
          <div className="w-24 h-1 bg-gradient-to-r from-teal-400 to-teal-600 mx-auto mt-4 rounded-full"></div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 border border-teal-100">
          
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              Welcome to Metafit Wellness by Romeja Wellness LLP. These Terms of Use ("Terms") govern your use of our platform and services provided by Book My Adventure ("we," "us," or "our"). By using our services, you agree to adhere to these Terms, which are designed to ensure a safe and enjoyable experience for all users.
            </p>
          </section>

          {/* Acceptance of Terms */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Acceptance of Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              By accessing or using our services, you agree to comply with and be bound by these Terms. If you disagree with any part of these Terms, please refrain from using our services. Your continued use of our services after any modifications to these Terms will constitute your acknowledgment of the modified Terms and your consent to abide by and be bound by the updated Terms.
            </p>
          </section>

          {/* Account Registration */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Account Registration</h2>
            <p className="text-gray-600 leading-relaxed">
              You may need to register for a subscription-based account to access certain features of our service. You agree to provide accurate, current, and complete information during registration and to update such information as necessary to maintain its accuracy. Your registration and account are personal to you and may not be transferred or shared with others.
            </p>
          </section>

          {/* Use of Services */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Use of Services</h2>
            <p className="text-gray-600 leading-relaxed">
              You agree to use the services only for lawful purposes and in a way that does not infringe on the rights of others or restrict or inhibit anyone else's use of the services. You are responsible for maintaining the security of your account and password and for all activities that occur under your account. We reserve the right to suspend or terminate your access if we suspect any unauthorized use or security breach.
            </p>
          </section>

          {/* Fees and Payment */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Fees and Payment</h2>
            <div className="bg-gradient-to-r from-teal-50 to-green-50 rounded-lg p-6 border border-teal-200">
              <p className="text-gray-600 leading-relaxed">
                If our services require payment, you agree to pay all applicable fees as described on our application. Any payment made is non-refundable unless otherwise stated. We may update our fees from time to time, and you will be notified of any changes before they take effect.
              </p>
            </div>
          </section>

          {/* Intellectual Property */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Intellectual Property</h2>
            <p className="text-gray-600 leading-relaxed">
              The services and all related content, features, and functionality are and will remain the exclusive property of Romeja Wellness LLP. You may not reproduce, distribute, modify, create derivative works of, publicly display, or use the content except as expressly permitted by these Terms. Unauthorized use of our intellectual property may result in legal action.
            </p>
          </section>

          {/* Data Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Data Privacy</h2>
            <p className="text-gray-600 leading-relaxed">
              We are committed to protecting your privacy. Our Privacy Policy governs the collection, use, and sharing of your information. By using our services, you consent to the collection, use, and sharing of your information as described in our Privacy Policy. We may update our Privacy Policy from time to time, and you should review it periodically.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Limitation of Liability</h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <p className="text-gray-600 leading-relaxed">
                We will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses. Our total liability for any claims arising out of your use of our services shall be limited to the amount you have paid to us for the service in question.
              </p>
            </div>
          </section>

          {/* Termination */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Termination</h2>
            <p className="text-gray-600 leading-relaxed">
              We reserve the right to terminate or suspend your access to our services at any time without notice for any reason, including but not limited to a violation of these Terms. Upon termination, your right to use the services will immediately cease, and we may, at our sole discretion, remove or delete your account and any associated data.
            </p>
          </section>

          {/* Governing Law */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Governing Law</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in India.
            </p>
          </section>

          {/* Cancellation and Refund Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Cancellation and Refund Policy</h2>
            <div className="bg-gradient-to-r from-teal-50 to-green-50 rounded-lg p-6 border border-teal-200">
              <p className="text-gray-600 leading-relaxed mb-4">
                At Metafit Wellness, we value your satisfaction and strive to offer flexible options for cancellations and refunds.
              </p>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Cancellation by Customers:</h4>
                  <p className="text-gray-600 leading-relaxed">
                    You may cancel booked services or sessions at least 24 hours prior to the scheduled time to be eligible for a full refund or rescheduling. Cancellations within 24 hours are non-refundable.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Subscriptions:</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Subscription plans may be canceled at any time. However, fees already paid are non-refundable for the remaining subscription period.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">No-shows:</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Failure to attend a booked session without prior notice is not eligible for any refund.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Cancellations by Metafit Wellness:</h4>
                  <p className="text-gray-600 leading-relaxed">
                    In rare cases, we may cancel or reschedule services. You will be notified promptly and may choose to reschedule or receive a full refund.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Refunds:</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Approved refunds will be processed within 7–10 business days to the original payment method.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Contact:</h4>
                  <p className="text-gray-600 leading-relaxed">
                    To request a cancellation or refund, please email us at hello@metafitwellness.com.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Changes to This Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We reserve the right to update or modify our Privacy Policy at any time. Any changes will be communicated by posting the revised Privacy Policy on our website. We encourage you to review this policy periodically to stay informed about how we protect your information.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-gradient-to-r from-teal-50 to-green-50 rounded-lg p-6 border border-teal-200">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4">Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have any questions or concerns about this Privacy Policy, please contact us at hello@metafitwellness.com. We are committed to addressing your inquiries and ensuring your privacy is respected.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default CancellationRefundPolicy;